import React from 'react'

import { ModalInjectedProps, ConfirmationDialog } from '@sketch/components'
import {
  usePublishShareMutation,
  GetProfilePublicationsDocument,
  GetProfilePublicationsQueryVariables,
} from '@sketch/gql-types'
import { useToast } from '@sketch/toasts'
import styled from 'styled-components'

interface PublishDocumentNowModalProps extends ModalInjectedProps {
  shareName: string
  shareIdentifier: string
  workspaceIdentifier: string
  publicCommentsEnabled?: boolean
  publicInspectEnabled?: boolean
}

const Paragraph = styled.p`
  white-space: break-spaces;
`

/**
 * This modal only publishes the document without any extra logic as opposed to "PublishDocumentModal"
 * which has much more logic around publishing a document if the profile already exists or not.
 *
 * To avoid adding even more logic to the "PublishDocumentModal", this modal is a simpler version
 * that just handles the publishing of the document.
 */
export const PublishDocumentNowModal = ({
  hideModal,
  shareName,
  shareIdentifier,
  workspaceIdentifier,
  publicCommentsEnabled = false,
  publicInspectEnabled = false,
}: PublishDocumentNowModalProps) => {
  const { showToast } = useToast()

  const [publishShare, { loading: isPublishing }] = usePublishShareMutation({
    onError: 'show-toast',
    onCompleted: () => {
      showToast(`"${shareName}" successfully published`)
      hideModal()
    },
    refetchQueries: [
      {
        query: GetProfilePublicationsDocument,
        variables: {
          workspaceIdentifier,
        } as GetProfilePublicationsQueryVariables,
      },
    ],
    awaitRefetchQueries: true,
  })

  const handleConfirm = () => {
    publishShare({
      variables: {
        shareIdentifier,
        publicCommentsEnabled,
        publicInspectEnabled,
      },
    })
  }

  return (
    <ConfirmationDialog
      hideModal={hideModal}
      title={'Publish Document?'}
      onConfirm={handleConfirm}
      confirmButton={{
        text: 'Publish',
        variant: 'primary',
        loading: isPublishing,
      }}
      cancelButton={{
        text: 'Cancel',
        disabled: isPublishing,
      }}
    >
      <Paragraph>
        This document will be available for everyone to view on your public
        profile.
      </Paragraph>
      <Paragraph>
        The most recent saved or starred document versions will be visible and
        updates will appear automatically.
      </Paragraph>
    </ConfirmationDialog>
  )
}
